import React, { useMemo, useState, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { Text, DatePicker, Button } from 'mintel-react-ui';
import { mintelGray400 } from 'mintel-tokens';

import useTranslation from '../translations';
import { changeLanguage } from '../shared/utils';
import { DjangoPageMessages, DateField } from '../fields';
import { HiddenFields } from '../InputWrapper';
import PageWrapper from '../PageWrapper';
import styles from './OutOfOffice.scss';

export const getDateString = (isoDate: string, language = 'en'): string => {
  const date = new Date(isoDate.split('T')[0]);

  return date.toLocaleDateString(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

type DateObject = { day: number; month: number; year: number };

export const dateToObject = (dateString: string | number): DateObject => {
  const date = new Date(dateString);
  return {
    day: date.getUTCDate(),
    // JS Date months are 0-indexed
    month: date.getUTCMonth() + 1,
    year: date.getUTCFullYear(),
  };
};

export const dateObjectToString = ({ day, month, year }: DateObject): string =>
  `${year}-${month}-${day}`;

export type OutOfOfficeProps = DjangoPageMessages & {
  fields: {
    outOfOfficeStart: DateField;
    outOfOfficeEnd: DateField;
  };
  startDate: string;
  endDate?: string;
  targetUserIsSelf: boolean;
  targetUser: { id: number; email: string };
  cancelUrl: string;
  inOffice: boolean;
  language?: string;
};

const OutOfOffice = ({
  fields,
  startDate,
  endDate,
  targetUserIsSelf,
  targetUser,
  cancelUrl,
  inOffice,
  errors,
  info,
  language = 'en',
}: OutOfOfficeProps): JSX.Element => {
  const { t } = useTranslation();
  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  const [outOfOfficeStart, setOutOfOfficeStart] = useState(
    fields.outOfOfficeStart.value,
  );
  const [outOfOfficeEnd, setOutOfOfficeEnd] = useState(
    fields.outOfOfficeEnd.value,
  );

  const setDates = (start: DateObject, end: DateObject): void => {
    setOutOfOfficeStart(dateObjectToString(start));
    setOutOfOfficeEnd(dateObjectToString(end));
  };

  const message = useMemo(() => {
    if (startDate && endDate) {
      if (
        getDateString(startDate, language) === getDateString(endDate, language)
      ) {
        return (
          <Trans>
            Out of office is currently set for{' '}
            <b>{getDateString(startDate, language)}</b>
          </Trans>
        );
      }
      return (
        <Trans>
          Out of office is currently set from{' '}
          <b>{getDateString(startDate, language)}</b> to{' '}
          <b>{getDateString(endDate, language)}</b>.
        </Trans>
      );
    }
    if (targetUserIsSelf) {
      return t('Your access is currently disabled.');
    }
    return t('Access is currently disabled.');
  }, [startDate, endDate, targetUserIsSelf, language, t]);

  return (
    <PageWrapper
      errors={errors}
      infos={info}
      successMessage="Your new out of office settings have been saved"
    >
      <form method="POST" className={styles.root}>
        <Text.Heading2>{t('Out of office setup')}</Text.Heading2>
        <Text.Body2>
          {targetUserIsSelf
            ? t(
                "If you activate your Out of Office access, you'll be able to use your Mintel services while you're away from your primary office network.",
              )
            : t(
                "If you activate a user's Out of Office access, they'll be able to use Mintel services while they're away from their primary office network.",
              )}
        </Text.Body2>
        <Text.Body2>
          {t(
            'You may not set out of office duration for more than 2 weeks at a time.',
          )}
        </Text.Body2>
        <Text.Body2>
          <ul>
            <li data-testid="message">{message}</li>
          </ul>
        </Text.Body2>
        {inOffice && (
          <div>
            <div className={styles.formHeading}>
              <Text.Heading5>
                {targetUserIsSelf ? (
                  t('Schedule Out of Office access')
                ) : (
                  <Trans>
                    Schedule Out of Office access for {targetUser.email}
                  </Trans>
                )}
              </Text.Heading5>
            </div>
            <div
              aria-hidden
              onClick={(e) => {
                // The DatePicker submits the form upon opening for some reason
                // this stops that happening
                e.preventDefault();
              }}
            >
              <div className={styles.datePickerLabel}>
                <Text.Link color={mintelGray400}>
                  {t('Select date range (2 week maximum):')}
                </Text.Link>
              </div>
              <DatePicker
                onSetRange={setDates}
                start={dateToObject(outOfOfficeStart)}
                end={dateToObject(outOfOfficeEnd)}
                showDays
                minDate={dateToObject(Date.now())}
              />
              <input
                hidden
                onChange={() => {}}
                name={fields.outOfOfficeEnd.name}
                id={fields.outOfOfficeEnd.id}
                value={outOfOfficeEnd}
              />
              <input
                hidden
                onChange={() => {}}
                name={fields.outOfOfficeStart.name}
                id={fields.outOfOfficeStart.id}
                value={outOfOfficeStart}
              />
            </div>
          </div>
        )}
        <div className={styles.buttonRow}>
          <div className={styles.button}>
            <Button.CancelButton
              fullWidth
              handleClick={() => {
                window.location.pathname = cancelUrl;
              }}
            >
              {t('Return')}
            </Button.CancelButton>
          </div>
          {inOffice && (
            <div className={styles.button}>
              <Button.ActionButton
                type="submit"
                fullWidth
                handleClick={() => {}}
              >
                {t('Schedule Out of Office')}
              </Button.ActionButton>
            </div>
          )}
        </div>
        <HiddenFields fields={fields} />
      </form>
    </PageWrapper>
  );
};

export default OutOfOffice;
